import { useLocalization } from "@progress/kendo-react-intl";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ListView, ListViewFooter, ListViewItemProps } from "@progress/kendo-react-listview";
import { SvgIcon } from "@progress/kendo-react-common";
import { Avatar } from "@progress/kendo-react-layout";
import { plusIcon, saveIcon, searchIcon, trashIcon } from "@progress/kendo-svg-icons";
import { TextBox, TextBoxChangeEvent, TextBoxProps } from "@progress/kendo-react-inputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { useLocation, useOutletContext } from "react-router-dom";
import { loadingPanel } from "../../LoadingPanel";


/**
 * ConfigDevicesManagement is a component responsible for managing the configuration
 * of devices. It interacts with an API to fetch and manipulate device data, including
 * adding, updating, and disabling devices. The component maintains various states
 * for managing device lists, selected device details, loading states, and notifications.
 * It also utilizes localization services for displaying language-specific strings.
 */

export const ConfigDevicesManagement = () => {

    const outletContext = useOutletContext<{ drawerExpanded?: boolean } | null>();
    const drawerExpanded = Boolean(outletContext?.drawerExpanded);

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const COLORS = {
        primary: '#018CA5',
        accent: '#198793',
        selectedBg: '#198792',
        panelBg: '#F7F7F7',
        title: '#1A8893',
        white: '#ffffff',
        markerRed: '#FF0000',
        borderMuted: '#ccc'
    };

    const [devicesList, setDevicesList] = useState<any>(null);
    const [counterActiveDevices, setCounterActiveDevices] = useState(null);
    const [counterInactiveDevices, setCounterInactiveDevices] = useState(null);
    const [deviceToDisable, setDeviceToDisable] = useState<any>(null);
    const [deviceMacSelected, setDeviceMacSelected] = useState('');
    const [deviceTypeName, setDeviceTypeName] = useState('');
    const [deviceNameSelected, setDeviceNameSelected] = useState<TextBoxProps["value"]>('');
    const [deviceVersionSelected, setDeviceVersionSelected] = useState<TextBoxProps["value"]>('');

    const [batterySelected, setBatterySelected] = useState('');
    const [lastCommunicationTimeSelected, setLastCommunicationTimeSelected] = useState<any>('');
    const [mapIdSelected, setMapIdSelected] = useState<any>('');
    const [deviceSerialNumberSelected, setDeviceSerialNumberSelected] = useState<TextBoxProps["value"]>('');
    const [deviceIpSelected, setDeviceIpSelected] = useState<TextBoxProps["value"]>('');
    const [deviceNfcSelected, setDeviceNfcSelected] = useState<TextBoxProps["value"]>('');

    const [listViewData, setListViewData] = useState(devicesList);
    const [groupsList, setGroupsList] = useState<any>([]);
    const [groupNameSelected, setGroupNameSelected] = useState<any>(null);
    const [groupIdSelected, setGroupIdSelected] = useState<any>(null);

    const [jobsList, setJobsList] = useState<any>(null);
    const [jobSelected, setJobSelected] = useState<any>(null);
    const [currentJobSelected, setCurrentJobSelected] = useState<any>(null);
    const [jobIdSelected, setJobIdSelected] = useState<any>(null);
    const [jobsListName, setJobsListName] = useState<any>(null);

    const [messageDisabledDevice, setMessageDisabledDevice] = useState<boolean>(false);
    const [showPopupDisableDevice, setShowPopupDisableDevice] = useState<boolean>(false);

    const [messageAddedDevice, setMessageAddedDevice] = useState<boolean>(false);
    const [messageSavedDevice, setMessageSavedDevice] = useState<boolean>(false);
    const [messageUpdatingDevice, setMessageUpdatingDevice] = useState<boolean>(false);
    const [emptyNameDevice, setEmptyNameDevice] = useState<boolean>(true);

    const [error, setError] = useState('');

    const [showPopupAddDevice, setShowPopupAddDevice] = useState<boolean>(false);
    const [newDeviceMac, setNewDeviceMac] = useState<string>('');
    const [filterValue, setFilterValue] = useState("");

    const localizationService = useLocalization();

    const [instance, accessTokenRequest] = AccessToken();

    let languageId = localStorage.getItem('languageId');
    const userType = localStorage.getItem('userType');
    const canEditIpNfc = userType?.includes('Admin');
    const canEditSerialNumber = canEditIpNfc;


    const [loading, setLoading] = useState(false);
    const devicesListContainerRef = useRef<HTMLDivElement | null>(null);
    const listScrollTopRef = useRef(0);

    const location = useLocation();
    // get deviceMac from synoptic grid devices
    let deviceMac = location?.state?.deviceMac;


    const getListScrollElement = () => {
        const container = devicesListContainerRef.current;
        if (!container) {
            return null;
        }

        return (container.querySelector('.k-listview-content') as HTMLElement | null) ?? container;
    };

    const rememberListScrollPosition = () => {
        const scrollElement = getListScrollElement();
        if (!scrollElement) {
            return;
        }

        listScrollTopRef.current = scrollElement.scrollTop;
    };

    const restoreListScrollPosition = () => {
        const savedScrollTop = listScrollTopRef.current;

        const applyScroll = () => {
            const currentScrollElement = getListScrollElement();
            if (!currentScrollElement) {
                return;
            }

            currentScrollElement.scrollTop = savedScrollTop;
        };

        applyScroll();
        requestAnimationFrame(() => {
            applyScroll();

            // First selection also changes layout; apply twice to avoid initial jump-to-top.
            requestAnimationFrame(() => {
                applyScroll();
            });
        });
    };

    const getDeviceList = async (flag: boolean) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers,
                params: {
                    enabled: flag
                }
            })
                .then((res: any) => {

                    if (res == '' || res?.data?.length == 0) {
                        setLoading(false);
                        return
                    }
                    if (res.length == 0) {
                        setLoading(false);
                        return
                    }

                    const data: any = res?.data
                    if (flag) {
                        setCounterActiveDevices(data?.data?.length);
                        setDevicesList(data?.data);
                    } else {
                        setCounterInactiveDevices(data?.data?.length);
                    }
                    setListViewData(data?.data);
                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList(true)
    }, [])

    const hasAppliedDefaultSelectionRef = useRef(false);

    useEffect(() => {
        if (hasAppliedDefaultSelectionRef.current) {
            return;
        }

        if (deviceMacSelected !== '') {
            hasAppliedDefaultSelectionRef.current = true;
            return;
        }

        if (!Array.isArray(devicesList) || devicesList.length === 0) {
            return;
        }

        hasAppliedDefaultSelectionRef.current = true;
        getDeviceMacSelected(devicesList[0]);
    }, [devicesList, deviceMacSelected]);


    const onFilterChange = (ev: any) => {
        let value = ev.value;
        setFilterValue(value);
        const newData = devicesList.filter((device: any) => {
            return (
                device.name?.toLowerCase().includes(value.toLowerCase()) ||
                device.mac?.toLowerCase().includes(value.toLowerCase())
            );
        });
        setListViewData(newData);
    };

    const clearFilter = () => {
        setFilterValue("");          // azzera il campo input
        getDeviceList(true);
    };

    const MyFooter = useCallback(() => {
        return (
            <ListViewFooter
                style={{ color: COLORS.primary, fontSize: 14 }}
                className="pl-3 pb-2 pt-2"
            >
            </ListViewFooter>
        );
    }, [COLORS.primary]);

    const toggleDialog = (item?: any) => {
        setDeviceToDisable(item);
        setShowPopupDisableDevice(!showPopupDisableDevice);
    };

    const disableDevice = async () => {

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            axios.put(`${SERVER_URL}/device/${deviceToDisable.mac}/false`, null, { headers })
                .then(() => {
                    getDeviceList(true)
                    setDeviceMacSelected('');
                    setMessageDisabledDevice(true);
                    if (!messageAddedDevice) {
                        setTimeout(() => {
                            setMessageDisabledDevice(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        onToggleMessage()
    }

    // called on click device
    const getDeviceMacSelected = (device: any) => {
        rememberListScrollPosition();
        setDeviceMacSelected(device.mac);
        setDeviceNameSelected(device.name);
        setDeviceTypeName(device.deviceTypeName);
        setDeviceVersionSelected(device.version);
        setDeviceSerialNumberSelected(device.serialNumber ?? '');
        setDeviceIpSelected(device.ip);
        setDeviceNfcSelected(device.nfc);

        setGroupNameSelected(device.groupName);
        setGroupIdSelected(device.groupId);
        setJobIdSelected(device.jobId);

        setBatterySelected(device.battery);
        setLastCommunicationTimeSelected(device.lastCommunicationTime);
        setMapIdSelected(device.mapId);
        setDeviceSerialNumberSelected(device.serialNumber ?? '');

        getGroupList();
        getJobsByGroup(device);
    }

    useEffect(() => {
        if (deviceMacSelected) {
            restoreListScrollPosition();
        }
    }, [deviceMacSelected]);

    const DevicesRender = useCallback((props: ListViewItemProps, index: number) => {
        let device = props.dataItem;
        const isSelected = device.mac === deviceMacSelected;
        return (
            <div
                key={index}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    gap: '8px',
                    //paddingRight: '10px',
                    cursor: `${userType === 'StandardOnlyLocator' || userType === 'UserAdminOnlyLocator' ? 'default' : 'pointer'}`,
                }}
            >
                <div
                    className="trash-icon"
                    style={{
                        float: 'left',
                        alignContent: 'center',
                        pointerEvents: `${userType?.startsWith('UbiquicomAdmin') ? 'auto' : 'none'}`,
                        opacity: `${userType?.startsWith('UbiquicomAdmin') ? 1 : 0.5}`
                    }}
                    onClick={() => { toggleDialog(device) }}
                >
                    <Avatar
                        type="image"
                        style={{ cursor: 'pointer' }}
                        className="trash-icon"
                    >
                        <SvgIcon icon={trashIcon} />
                    </Avatar>
                </div>
                <p
                    className="devices-mgmt-device-item"
                    ref={(el) => (itemRefs.current[device.id] = el)}
                    style={{
                        cursor: 'pointer',
                        margin: '0px',
                        padding: '10px',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        float: 'left',
                        alignContent: 'center',
                        //paddingRight: '10px',
                        fontSize: `${deviceMacSelected != '' ? 'clamp(10px, 0.8vw, 16px)' : 'clamp(9px, 0.7vw, 16px)'}`,
                        //pointerEvents: `${userType === 'StandardOnlyLocator' ? 'none' : 'auto'}`,
                        //opacity: `${userType === 'StandardOnlyLocator' ? 0.5 : 1}`,
                        background: isSelected ? COLORS.selectedBg : COLORS.white,
                        color: !isSelected ? COLORS.selectedBg : COLORS.white
                    }}
                    onClick={(e) => {
                        getDeviceMacSelected(device);
                        //const title = e.currentTarget.querySelector('.device-management-name-h2') as HTMLElement | null;
                        //title?.focus({ preventScroll: true });
                    }}
                >
                    {device.name} ({device.mac})
                </p>
            </div>
        );
    }, [deviceMacSelected, userType]);

    const devicesListData = useMemo(() => listViewData ?? devicesList, [listViewData, devicesList]);

    const devicesListView = useMemo(() => {
        return (
            <ListView
                className="k-listview-item row p-2 align-middle devices-mgmt-toolbar-row"
                data={devicesListData}
                item={DevicesRender}
                style={{ background: COLORS.panelBg }}
                footer={MyFooter}
            />
        );
    }, [devicesListData, DevicesRender, COLORS.panelBg, MyFooter]);

    const handleChangeDeviceName = useCallback((event: TextBoxChangeEvent) => {
        setDeviceNameSelected(event.target.value);
        setEmptyNameDevice(false);
    }, []);

    const handleChangeDeviceVersion = useCallback((event: TextBoxChangeEvent) => {
        setDeviceVersionSelected(event.target.value);
    }, []);

    const handleChangeDeviceSerialNumber = useCallback((event: TextBoxChangeEvent) => {
        setDeviceSerialNumberSelected(event.target.value);
    }, []);

    const handleChangeDeviceIp = useCallback((event: TextBoxChangeEvent) => {
        setDeviceIpSelected(event.target.value);
    }, []);

    const handleChangeDeviceNfc = useCallback((event: TextBoxChangeEvent) => {
        setDeviceNfcSelected(event.target.value);
    }, []);

    const getGroupList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/list?enabled=true`, { headers })
                .then((res) => {
                    const data = res.data.data

                    // add no group to the list
                    data.unshift({
                        id: '-1',
                        name: localizationService.toLanguageString('configuration.devices.no_group', 'NOT_FOUND')
                    })

                    setGroupsList(data);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getDeviceDetailsFromSynoptic = async (deviceMac: any) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers
            })
                .then((res) => {
                    const data: any = res?.data.data

                    const currentDevice = data.filter((device: any) => device?.mac === deviceMac);
                    setDeviceMacSelected(currentDevice[0].mac);
                    setDeviceNameSelected(currentDevice[0].name);
                    setDeviceTypeName(currentDevice[0].deviceTypeName);
                    setDeviceVersionSelected(currentDevice[0].version);
                    setGroupNameSelected(currentDevice[0].groupName);
                    setGroupIdSelected(currentDevice[0].groupId);
                    setBatterySelected(currentDevice[0].battery);
                    setLastCommunicationTimeSelected(currentDevice[0].lastCommunicationTime);
                    setMapIdSelected(currentDevice[0].mapId);
                    setDeviceSerialNumberSelected(currentDevice[0].serialNumber ?? '');

                    getJobsByGroupId(currentDevice[0].groupId);
                    getDeviceByMac(currentDevice[0].mac, true);
                    getGroupList();

                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        getDeviceDetailsFromSynoptic(deviceMac);
    }, [deviceMac])

    useEffect(() => {
        getJobList(true);
    }, []);

    const getJobList = async (flag: boolean) => {

        setLoading(true);
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/job/list?enabled=${flag}`, { headers })
                .then((res) => {
                    const data = res?.data?.data
                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    let jobList: any = []
                    data.forEach((job: any) => {
                        jobList.push(job)
                    });
                    setJobsList(jobList);
                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    };

    const getDeviceByMac = async (mac: any, active: any) => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            axios.get(`${SERVER_URL}/Device/${mac}`, { headers })
                .then((res) => {
                    const data = res.data.data

                    if (data.groupId == 0) {
                        setGroupNameSelected(localizationService.toLanguageString('configuration.devices.no_group', 'NOT_FOUND'));
                    }

                    let selectedJob: any = {
                        id: data.jobId,
                        name: data.jobName
                    }
                    if (!data.jobId || !data.jobName) {
                        selectedJob = {
                            id: '-1',
                            name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND')
                        }
                    }
                    setJobSelected(selectedJob);
                    setCurrentJobSelected(selectedJob);
                    setJobIdSelected(selectedJob.id);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getJobsByGroup = async (device: any) => {

        if (device.groupId == -1 || device.groupId == 0) {
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/${device.groupId}/jobs`, { headers })
                .then((res) => {
                    const result = res.data.data
                    const data = result.filter((item: any) => item !== null);

                    setJobSelected([])
                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    setJobsListName(data)

                    getDeviceByMac(device.mac, true);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getJobsByGroupId = async (groupId: any, deviceMacSelected?: any) => {

        if (groupId == -1) {
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/${groupId}/jobs`, { headers })
                .then((res) => {
                    const result = res.data.data
                    const data = result.filter((item: any) => item !== null);

                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    setJobsListName(data)

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const handleChangeGroup = (group: any) => {
        setGroupIdSelected(group.id);
        setGroupNameSelected(group.name);

        getJobsByGroupId(group.id, deviceMacSelected);
        setJobSelected(-1)
        setJobIdSelected(-1)
    }


    const handleChangeJob = useCallback((event: DropDownListChangeEvent, jobsListName: any) => {
        const currentJobSelected = event.value
        setJobSelected(currentJobSelected);
        setJobIdSelected(currentJobSelected.id)
    }, [])

    const CustomTitle = () => {
        return (
            <div
                className="custom-title"
                style={{ fontSize: "18px", lineHeight: "1.3em" }}
            >
                {localizationService.toLanguageString('configuration.devices.disable_device', 'NOT_FOUND')} "{deviceToDisable.name} ({deviceToDisable.mac})" ?
            </div>
        );
    };

    const onToggleMessage = () => {
        setMessageDisabledDevice(true);
        if (!messageDisabledDevice) {
            setTimeout(() => {
                setMessageDisabledDevice(false);
            }, 3000);
        }
    };

    const openPopupAddDevice = () => {
        setShowPopupAddDevice(!showPopupAddDevice);
    }

    const addDevice = async () => {

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            axios.post(`${SERVER_URL}/device/${newDeviceMac}`, null, { headers })
                .then(() => {
                    setDeviceNameSelected('');
                    setDeviceVersionSelected('');
                    setGroupNameSelected('');
                    setGroupIdSelected('');
                    setMessageAddedDevice(true);
                    getDeviceList(true)
                    getGroupList()
                    setDeviceMacSelected(newDeviceMac)


                    setMessageSavedDevice(true);
                    if (!messageSavedDevice) {
                        setTimeout(() => {
                            setMessageSavedDevice(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        if (!messageAddedDevice) {
            setTimeout(() => {
                setMessageAddedDevice(false);
            }, 3000);
        }

    }

    const updateDevice = async () => {

        setLoading(true);

        const normalizedGroupId = Number(groupIdSelected);
        const normalizedJobId = Number(jobIdSelected);
        const fallbackJobId = Number(jobSelected?.id ?? currentJobSelected?.id);

        const resolvedJobId = Number.isFinite(normalizedJobId)
            ? normalizedJobId
            : Number.isFinite(fallbackJobId)
                ? fallbackJobId
                : null;

        let params = {
            name: deviceNameSelected,
            //version: deviceVersionSelected,
            groupId: groupIdSelected > 0 ? groupIdSelected : null,
            jobId: groupIdSelected == '-1' ? null : jobIdSelected > 0 ? jobIdSelected : null,
            serialNumber: deviceSerialNumberSelected,
            ip: deviceIpSelected,
            nfc: deviceNfcSelected
        };

        if (normalizedGroupId === -1) {
            params.jobId = null;
        } else if (resolvedJobId !== null && resolvedJobId > 0) {
            params.jobId = resolvedJobId;
        } else if (resolvedJobId === -1) {
            params.jobId = null;
        }


        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.put(`${SERVER_URL}/device/${deviceMacSelected}`, params, { headers })
                setMessageUpdatingDevice(true);

                clearFilter()

                if (!messageUpdatingDevice) {
                    setTimeout(() => {
                        setMessageUpdatingDevice(false);
                    }, 3000);
                }

                setLoading(false);
            } catch (error: any) {
                setError(error);
                setLoading(false);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }

    }

    const itemRefs: any = useRef([]);

    const devicesManagementCss = useMemo(() => `
        .devices-mgmt-root {
            display: flex;
            gap: 12px;
            overflow: auto;
            height: 100%;
            min-height: 0;
            align-items: stretch;
            width: 100%;
            padding: 10px;
            box-sizing: border-box;
        }

        .devices-mgmt-root.no-selection .devices-mgmt-left {
            flex: 1.5 1 0px;
        }
            
        .devices-mgmt-root.has-selection {
            flex-direction: row;
        }

        .devices-mgmt-root.has-selection .devices-mgmt-left {
            flex: 1.5 1 0px;
        }

        .devices-mgmt-root.has-selection .devices-mgmt-right {
            flex: 1 1 0;
            min-width: 0;
        }

        .devices-mgmt-left {
            flex: 1.5 1 0px;
            border-radius: 14px;
            overflow-y: hidden;
            overflow-x: hidden;
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
            padding: 10px;
            background: #F7F7F7;
            box-sizing: border-box;
            border: 1px solid #d6e8ee;
            box-shadow: 0 10px 24px rgba(10, 55, 75, 0.08);
        }

        .devices-mgmt-title {
            font-weight: 900;
            letter-spacing: 0.55px;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.7);
            margin: 0;
        }

        .devices-mgmt-toolbar {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        .devices-mgmt-toolbar .k-textbox {
            flex: 1;
            min-width: 180px;
        }

        .devices-mgmt-list {
            margin-top: 4px;
            flex: 1 1 auto;
            min-height: 0;
        }

        .devices-mgmt-left .k-listview {
            height: 94%;
            overflow-y: auto;
        }

        .devices-mgmt-device-item {
            background: #ffffff;
            border: 1px solid #cfe3ea;
            border-radius: 12px;
            padding: 6px 8px;
            box-shadow: 0 3px 10px rgba(10, 55, 75, 0.06);
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .devices-mgmt-device-item:hover {
            transform: translateY(-1px);
            box-shadow: 0 8px 18px rgba(10, 55, 75, 0.12);
        }

        .device-management-name-h2 {
            border-radius: 10px;
            transition: background 120ms ease, color 120ms ease;
            outline: none;
        }

        .device-management-name-h2.is-selected,
        .device-management-name-h2:focus-visible {
            background: #e1f3f7;
            color: #0b5f70;
            box-shadow: inset 0 0 0 1px #99d4df;
        }

        .devices-mgmt-toolbar-row {
            display: block;
            width: 100%;
            margin: 0;
            background: transparent;
        }


        .devices-mgmt-right {
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            flex-grow: 2;
            align-items: stretch;
            border: 1px solid #cfe3ea;
            box-shadow: rgba(0, 0, 0, 0.08) 0px 6px 18px;
            border-radius: 12px;
            box-shadow: 0 10px 24px rgba(15, 50, 80, 0.06);
            padding: 12px;
            min-width: 0;
            height: 100%;
        }

        .devices-mgmt-right .k-listview-item {
            width: 100%;
            margin-top: 20px;
        }

        .devices-mgmt-right fieldset {
            border: 1px solid #d4e3f1;
            border-radius: 12px;
            padding: 12px;
            background: #ffffff;
        }

        .devices-mgmt-save-row {
            justify-content: flex-end;
            margin: 0px;
        }

        .devices-mgmt-root .k-button {
            transition: transform 120ms ease, box-shadow 180ms ease;
        }

        .devices-mgmt-root .k-button:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 8px 16px rgba(15, 50, 80, 0.14);
        }

        .k-avatar-image {
            width: auto;
        }

        @media (max-width: 1200px) {
            .devices-mgmt-root {
                flex-direction: column;
                height: calc(100vh - 140px);
            }

            .devices-mgmt-left {
                width: 100%;
                flex-basis: auto;
                height: 50%;
            }

            .devices-mgmt-right {
                width: 100%;
                height: 50%;
            }
        }

        @media (max-width: 760px) {
            .devices-mgmt-toolbar {
                flex-wrap: wrap;
            }

            .devices-mgmt-left,
            .devices-mgmt-right {
                border-radius: 10px;
            }
        }
    `, []);

    return (
        <>
            <style>{devicesManagementCss}</style>

            {loading && loadingPanel}
            {devicesList?.length == 0 || devicesList == null &&
                // Mostra un messaggio o un altro contenuto se non ci sono dati
                <div style={{ textAlign: 'center', padding: '20px', border: `1px dashed ${COLORS.borderMuted}` }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            {devicesList?.length > 0 &&
                <div className={`devices-mgmt-root ${deviceMacSelected ? "has-selection" : "no-selection"}`}
                >

                    <NotificationGroup
                        style={{
                            right: 0,
                            bottom: 50,
                            zIndex: 1200,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {messageDisabledDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageDisabledDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.device_disabled', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageAddedDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageAddedDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.added_device', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageSavedDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageSavedDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.saved_device', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageUpdatingDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageUpdatingDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.updated_device', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                        </Fade>
                    </NotificationGroup>

                    {/* left panel */}
                    <div
                        className="devices-mgmt-left"
                    >
                        {loading && loadingPanel}
                        <div style={{ textAlign: 'center', marginBottom: '20px', justifyContent: 'space-between' }}>
                            <div >
                                <p
                                    className="devices-mgmt-title"
                                    style={{ display: 'inline-flex', textAlign: 'center', fontSize: '20px', color: COLORS.title }}
                                >
                                    {localizationService.toLanguageString('configuration.devices.devices_list', 'NOT_FOUND')}
                                </p>
                                <p
                                    className="devices-mgmt-title"
                                    style={{ display: 'inline-flex', textAlign: 'center', fontSize: '20px', color: COLORS.title }}
                                >
                                    ({counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')})
                                </p>


                                <Button
                                    themeColor={"primary"}
                                    className="plusParam"
                                    onClick={() => { openPopupAddDevice() }}
                                    style={{ marginTop: '0px' }}
                                    title={localizationService.toLanguageString('configuration.devices.add_device', 'NOT_FOUND')}
                                >
                                    <Avatar
                                        className="plusParam"
                                    >
                                        <SvgIcon
                                            icon={plusIcon}
                                            style={{
                                                margin: '8px'
                                            }}
                                        />
                                    </Avatar>
                                </Button>
                            </div>
                        </div>

                        <div
                            ref={devicesListContainerRef}
                            style={{
                                flex: 1,
                                minHeight: 0,
                                padding: '8px',
                                width: '100%',
                                display: 'flex',
                                //flexDirection: 'column'
                            }}
                        >
                            <div
                                className="k-listview-item row p-2 align-middle devices-mgmt-toolbar-row"
                            >
                                <fieldset className="devices-mgmt-toolbar" style={{ display: 'flex' }} >
                                    <TextBox
                                        style={{
                                            border: `2px solid ${COLORS.borderMuted}`,
                                            boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                            marginBottom: '10px',
                                        }}
                                        value={filterValue}
                                        onChange={onFilterChange}
                                        placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT_FOUND')}
                                        prefix={() => (
                                            <SvgIcon icon={searchIcon} svgStyle={{ margin: drawerExpanded ? '20px -5px 0px 5px' : '0px -5px 0px 5px' }} />
                                        )}
                                    />
                                </fieldset>


                                {devicesListView}
                            </div>
                        </div>
                        {showPopupAddDevice && (
                            <Dialog
                                title={localizationService.toLanguageString('configuration.devices.add_device', 'NOT_FOUND')}
                                onClose={openPopupAddDevice}
                                contentStyle={{ color: 'black' }}
                            >
                                <TextBox
                                    style={{
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    onChange={(e: any) => setNewDeviceMac(e.target.value)}
                                    placeholder={localizationService.toLanguageString('configuration.devices.mac', 'NOT_FOUND')}
                                />
                                <div className='k-window-content k-buttons-container' >
                                    <button
                                        className="add-device-button"
                                        onClick={openPopupAddDevice}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT_FOUND')}
                                    </button>
                                    <button
                                        className="add-device-button"
                                        onClick={() => {
                                            if (!showPopupDisableDevice) {
                                                openPopupAddDevice()
                                                addDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT_FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                        {showPopupDisableDevice && (
                            <Dialog
                                className="confirm-action-dialog"
                                title={<CustomTitle />}
                                onClose={toggleDialog}
                            >
                                <div className="dialog-actions">
                                    <button
                                        className="disable-device-button"
                                        onClick={toggleDialog}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT_FOUND')}
                                    </button>
                                    <button
                                        className="disable-device-button"
                                        onClick={() => {
                                            if (showPopupDisableDevice) {
                                                toggleDialog()
                                                disableDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT_FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                    </div>

                    {/* right panel */}
                    {deviceMacSelected != '' &&
                        <div
                            className="devices-mgmt-right"
                            style={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 3,
                                alignItems: 'stretch'
                            }}
                        >
                            {/* mac + name */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <fieldset>
                                    <h1 className="k-h2" style={{ color: COLORS.primary }}>{localizationService.toLanguageString('configuration.devices.mac', 'NOT_FOUND')}</h1>
                                    <h5 className="k-h5" style={{ color: COLORS.primary }}>{deviceMacSelected != '' ? deviceMacSelected : localizationService.toLanguageString('configuration.devices.no_mac', 'NOT_FOUND')}</h5>

                                    <hr style={{ border: `1px solid ${COLORS.primary}`, width: '100%' }} />

                                    <div className="k-h5" style={{ color: COLORS.primary }}>{localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}</div>
                                    <TextBox
                                        value={deviceNameSelected ?? ''}
                                        onChange={handleChangeDeviceName}
                                        placeholder={localizationService.toLanguageString('configuration.devices.enter_device_name', 'NOT_FOUND')}
                                    />

                                    {(deviceTypeName != '' || deviceVersionSelected != '') &&
                                        <div
                                            className="k-listview-item row p-1 align-middle"
                                            style={{
                                                display: 'flex',
                                                gap: '18px',
                                                margin: 0,
                                                marginTop: '10px',
                                                cursor: 'default',
                                                flexWrap: 'wrap'
                                            }}
                                        >
                                            {/* device type */}
                                            {deviceTypeName != '' &&
                                                <div style={{ flex: '1 1 280px', minHeight: '40px' }}>
                                                    <div className="k-h5" style={{ display: 'flex', alignItems: 'baseline', color: '#018CA5' }}>
                                                        <span style={{ marginRight: '10px' }}>{localizationService.toLanguageString('configuration.devices.device_type', 'Device type')}:</span>
                                                        <p style={{ margin: 0 }}>{deviceTypeName ?? ''}</p>
                                                    </div>
                                                </div>
                                            }

                                            {/* version */}
                                            {deviceVersionSelected != '' &&
                                                <div style={{ flex: '1 1 280px', minHeight: '40px' }}>
                                                    <div className="k-h5" style={{ display: 'flex', alignItems: 'baseline', color: '#018CA5' }}>
                                                        <span style={{ marginRight: '10px' }}>{localizationService.toLanguageString('configuration.devices.version', 'NOT_FOUND')}:</span>
                                                        <p style={{ margin: 0 }}>{deviceVersionSelected ?? ''}</p>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    }

                                    {/* serial Ip/Nfc */}
                                    <div
                                        className="k-listview-item row p-2 align-middle"
                                        style={{ display: 'flex', flexDirection: 'column', gap: '12px', margin: '10px auto' }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div className="k-h5" style={{ color: COLORS.primary, margin: 0 }}>
                                                {localizationService.toLanguageString('synoptic_devices.serialNumber', 'Serial Number')}
                                            </div>
                                            <TextBox
                                                value={deviceSerialNumberSelected ?? ''}
                                                onChange={handleChangeDeviceSerialNumber}
                                                disabled={!canEditSerialNumber}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: '1 1 0px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <div className="k-h5" style={{ color: COLORS.primary, margin: 0 }}>
                                                    {localizationService.toLanguageString('configuration.devices.ip', 'IP')}
                                                </div>
                                                <TextBox
                                                    value={deviceIpSelected ?? ''}
                                                    onChange={handleChangeDeviceIp}
                                                    disabled={!canEditIpNfc}
                                                />
                                            </div>

                                            <div style={{ flex: '1 1 0px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <div className="k-h5" style={{ color: COLORS.primary, margin: 0 }}>
                                                    {localizationService.toLanguageString('configuration.devices.nfc', 'NFC')}
                                                </div>
                                                <TextBox
                                                    value={deviceNfcSelected ?? ''}
                                                    onChange={handleChangeDeviceNfc}
                                                    disabled={!canEditIpNfc}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </fieldset>

                            </div>

                            {/* groups */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <fieldset>
                                    <div className="k-h5" style={{ color: COLORS.primary, marginTop: '10px' }}>{localizationService.toLanguageString('configuration.devices.groups', 'NOT_FOUND')}</div>
                                    <DropDownList
                                        data={groupsList}
                                        textField="name"
                                        dataItemKey="id"
                                        value={groupsList.find((g: any) => g.id === groupIdSelected) || null}
                                        onChange={(e: any) => handleChangeGroup(e.value)}
                                        style={{ width: "100%", color: COLORS.primary, cursor: 'pointer' }}
                                    />
                                </fieldset>
                            </div>

                            {/* jobs */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                {groupIdSelected != 0 && groupIdSelected != -1 && jobsListName?.length > 0 &&
                                    <fieldset>
                                        <div className="k-h5" style={{ color: COLORS.primary }}>{localizationService.toLanguageString('configuration.devices.jobs', 'NOT_FOUND')}</div>
                                        <DropDownList
                                            data={jobsListName ?? []}
                                            dataItemKey="id"
                                            defaultValue={jobsListName[0]}
                                            value={!jobSelected?.name ? jobsListName[0] : jobSelected}
                                            textField={'name'}
                                            onChange={(e: any) => handleChangeJob(e, jobsListName)}
                                            style={{ width: "100%", color: COLORS.primary, cursor: 'pointer' }}
                                        />
                                    </fieldset>
                                }

                                {/*  save button */}
                                <div className="k-listview-item row p-3 devices-mgmt-save-row">
                                    <Button
                                        className="save-devices-management-button"
                                        themeColor={"primary"}
                                        svgIcon={saveIcon}
                                        onClick={() => updateDevice()}
                                    >
                                        {localizationService.toLanguageString('configuration.devices.save', 'NOT_FOUND')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }
        </>
    )
}
